import _ from 'lodash';
import {
  OrderLeg,
  OrderAction,
  PositionEffect,
  AssetType,
  OrderType,
  Order,
  PnLItem,
  OrderStatuses,
  Durations,
  DebitOrCredit,
  OrderActivity,
  ResponseCode,
  ResponseCodes,
} from '@tradingblock/types';
import { TradingBlockAccountApi, PnLItemGetPnLItemRequest, OrderStatusCategories } from '@tradingblock/api';
import { formatNumber } from '@tradingblock/components';
import { TradingBlockDispatcher } from '../dispatcher';
import { optionToDetailString, orderLegToDetails } from './options';
import { isErrorActivity } from '../../../utilities/activity';

/**
 * Multileg orders that include both stock and listed options: API often sends per-leg SpreadRatio
 * that does not encode 100:1 share/contract semantics (e.g. same ratio on both legs). For display,
 * use order spread quantity as contract count for option legs and shares = quantity × option multiplier.
 */
function getStockOptionComboDisplayQuantity(leg: OrderLeg, orderSpreadQuantity: number, legs: OrderLeg[]): number | null {
  if (legs.length < 2) {
    return null;
  }
  const hasEquityLeg = legs.some(l => l.AssetType === AssetType.Equity);
  const hasOptionLeg = legs.some(l => l.AssetType === AssetType.Option);
  if (!hasEquityLeg || !hasOptionLeg) {
    return null;
  }

  const optionLeg = legs.find(l => l.AssetType === AssetType.Option);
  const multiplierRaw = optionLeg != null ? optionLeg.Multiplier : undefined;
  const contractMultiplier = multiplierRaw != null && multiplierRaw > 0 ? multiplierRaw : 100;

  if (leg.AssetType === AssetType.Equity) {
    return orderSpreadQuantity * contractMultiplier;
  }
  if (leg.AssetType === AssetType.Option) {
    return orderSpreadQuantity;
  }
  return null;
}

/**
 * Returns the common SpreadRatio when every leg agrees (undefined treated as 1), else null.
 */
function getUniformSpreadRatio(legs: OrderLeg[]): number | null {
  if (legs.length === 0) {
    return null;
  }
  const first = legs[0].SpreadRatio != null ? legs[0].SpreadRatio : 1;
  const allMatch = legs.every(l => {
    const r = l.SpreadRatio != null ? l.SpreadRatio : 1;
    return r === first;
  });
  return allMatch ? first : null;
}

/**
 * Option-only multileg (vertical, condor, etc.): normally display = order qty × leg SpreadRatio.
 * Some API responses repeat the spread quantity in each leg's SpreadRatio (e.g. Q=5 and both legs 5);
 * multiplying would show 25 per leg while fills are 5. When all legs share the same ratio and it
 * equals order quantity, treat ratio as redundant and show order quantity only.
 */
function getOptionOnlyMultilegDisplayQuantity(leg: OrderLeg, orderQty: number, legs: OrderLeg[]): number {
  const uniform = getUniformSpreadRatio(legs);
  if (uniform !== null && uniform === orderQty) {
    return orderQty;
  }
  const r = leg.SpreadRatio != null ? leg.SpreadRatio : 1;
  return orderQty * r;
}

/**
 * This type contains the majority of the data needed
 * to fill out the "details" section of the table in the
 * ActivityOrders and ActivityTrades tab of the activity block
 */
interface ActivityOrderDetailProps {
  day?: string;
  month?: string;
  year?: string;
  strikePrice?: string;
  orderType?: string;
  symbol?: string;
  quantity?: string;
}

/**
 * This function takes in an OrderType and returns a formatted
 * string corresponding to that type.
 *
 * @param orderType The enum value given to any order.
 */
function orderTypeToString(orderType: OrderType): string {
  switch (orderType) {
    case OrderType.Market:
      return 'MKT';
    case OrderType.Limit:
      return 'LMT';
    case OrderType.Stop_Limit:
      return 'STOP LMT';
    case OrderType.Stop_Market:
      return 'STOP MKT';
    case OrderType.Market_On_Close:
      return 'MKT ON CLOSE';
    default:
      return 'MKT';
  }
}

export function formatOrderPrice(order: Pick<Order, 'OrderType' | 'DebitCredit' | 'Price' | 'Duration' | 'Stop'>) {
  const { Price, Duration, DebitCredit, Stop } = order;
  const orderType = order.OrderType;
  if (_.isNil(Price)) {
    return '';
  }

  const priceValue = Math.abs(Price);
  const price = formatNumber(priceValue, { currency: true });
  const stopValue = Stop ? Math.abs(Stop) : undefined;
  const stop = stopValue ? formatNumber(stopValue, { currency: true }) : '';
  const prefix = DebitCredit ? (DebitCredit === DebitOrCredit.Debit ? 'DBT' : 'CRDT') : '';
  const orderTypeString = orderTypeToString(orderType);
  const duration = Duration === Durations.GTC ? ' GTC' : '';

  if (orderType === OrderType.Stop_Limit) {
    return `${stop} ${orderTypeString} ${price} ${prefix} ${duration}`;
  } else if (orderType === OrderType.Stop_Market) {
    return `${stop} ${orderTypeString} ${prefix} ${duration}`;
  } else if (orderType === OrderType.Market) {
    return `${orderTypeString} ${prefix} ${duration}`;
  } else {
    return `${price} ${orderTypeString} ${prefix} ${duration}`;
  }
}

/**
 * Gets the total number of filled orders
 * @param legs An array of orderlegs
 * @param backUpQuantity in the event the orderLegs don't exist, this value will be treated as the fallback
 * @returns the total number of filled orders
 */
export function getFilledQuantity(legs: OrderLeg[], backUpQuantity: number) {
  let filledQuantity = backUpQuantity;
  if (legs === null || legs === undefined) {
    filledQuantity = backUpQuantity;
  } else {
    filledQuantity = _.sum(_.map(legs, l => l.LegFillQuantity));
  }

  return filledQuantity;
}

/**
 * Checks to see if the OrderStatus is either undefined, "New", "Pending New", or "Initiated"
 * @param orderStatus The number of the enum value corresponding with the OrderStatus
 */
export function isNewOrderStatus(orderStatus: number | undefined) {
  return _.includes([undefined, OrderStatuses.New, OrderStatuses.PendingNew, OrderStatuses.Initiated], orderStatus);
}

/**
 * Checks to see if the OrderStatus is "Cancelled"
 * @param orderStatus The number of the enum value corresponding with the OrderStatus
 */
export function isCancelledOrderStatus(orderStatus: number | undefined) {
  return _.includes([OrderStatuses.Cancelled], orderStatus);
}

/**
 * Checks to see if the OrderStatus is "Partially Filled"
 * @param orderStatus The number of the enum value corresponding with the OrderStatus
 */
export function isPartialOrderStatus(orderStatus: number | undefined) {
  return _.includes([OrderStatuses.PartiallyFilled], orderStatus);
}

export function isPartiallyCancelledOrderStatus(orderStatus: number | undefined) {
  return _.includes([OrderStatuses.PartiallyCancelled], orderStatus);
}

/**
 * Checks to see if the OrderStatus is "Filled"
 * @param orderStatus The number of the enum value corresponding with the OrderStatus
 */
export function isFilledOrderStatus(orderStatus: number | undefined) {
  return _.includes([OrderStatuses.Filled], orderStatus);
}

/**
 * Checks to see if the OrderStatus is new or partially filled
 * @param orderStatus The number of the enum value corresponding with the OrderStatus
 */
export function isNewOrPartialOrderStatus(orderStatus: number | undefined) {
  return isNewOrderStatus(orderStatus) || isPartialOrderStatus(orderStatus);
}

/**
 * Checks to see if the OrderStatus is new or cancelled
 * @param orderStatus The number of the enum value corresponding with the OrderStatus
 */
export function isNewOrCancelledOrderStatus(orderStatus: number | undefined) {
  //return isNewOrderStatus(orderStatus) || isCancelledOrderStatus(orderStatus);
  if (orderStatus === undefined) {
    return false;
  }
  return [OrderStatuses.New, OrderStatuses.Cancelled, OrderStatuses.Replaced].includes(orderStatus);
}

/**
 * This function calls the TradingBlock API and returns
 * an asynchronous function that allows you to get the
 * PnL items corresponding with the given account
 * @param _api the tradingblock api object
 * @param _dispatcher the dispatcher
 */
export function loadPnL(_api: TradingBlockAccountApi, _dispatcher: TradingBlockDispatcher) {
  return async (_accountId: number, request: PnLItemGetPnLItemRequest) => {
    _dispatcher.pnlHistory.request(_accountId);
    const _request = (await _api.pnl(request)).payload;

    const pnl = _request ? (_request.Items as PnLItem[]) : ([] as PnLItem[]);
    _dispatcher.pnlHistory.receive(_accountId, pnl);
    return pnl;
  };
}

export function formatOrderLeg(
  leg: OrderLeg,
  details: ActivityOrderDetailProps,
  order: OrderActivity | Order,
  showSymbol = false
) {
  const { Quantity, OrderStatus } = order;
  let orderAction = '';

  let isBuy = OrderAction[leg.Action] === 'Buy';
  if (isErrorActivity(order)) {
    orderAction = `Error ${isBuy ? 'buying' : 'selling'}`;
  } else if (_.isNil(OrderStatuses) || !OrderStatus) {
    orderAction = '';
  } else if (OrderStatusCategories.Filled.includes(OrderStatus)) {
    orderAction = isBuy ? 'Bought' : 'Sold';
  } else {
    orderAction = isBuy ? 'Buy' : 'Sell';
  }
  if (OrderStatus === OrderStatuses.Initiated) {
    orderAction = `Initiated ${_.toLower(orderAction)}`;
  }

  const e = PositionEffect[leg.PositionEffect];
  const s = leg.AssetType === AssetType.Equity ? leg.Symbol : details.symbol;
  const isMultileg = order.Legs != null && order.Legs.length > 1;
  const legs = order.Legs != null ? order.Legs : [];
  /**
   * Single-leg equity: Quantity is already shares; ignore SpreadRatio so stale ratios don’t inflate display.
   * Multileg (options / mixed): see getStockOptionComboDisplayQuantity and getOptionOnlyMultilegDisplayQuantity.
   */
  const isSingleEquityLeg = !isMultileg && leg.AssetType === AssetType.Equity;

  const useLegFillQty =
    leg.LegFillQuantity != null &&
    OrderStatus != null &&
    (OrderStatusCategories.Filled.includes(OrderStatus) ||
      isPartialOrderStatus(OrderStatus) ||
      isPartiallyCancelledOrderStatus(OrderStatus));

  let legSizeForDisplay: number;
  if (useLegFillQty) {
    legSizeForDisplay = leg.LegFillQuantity as number;
  } else if (leg.Quantity != null && leg.Quantity > 0) {
    legSizeForDisplay = leg.Quantity;
  } else {
    const stockOptionQty = getStockOptionComboDisplayQuantity(leg, Quantity, legs);
    if (stockOptionQty != null) {
      legSizeForDisplay = stockOptionQty;
    } else if (isSingleEquityLeg) {
      legSizeForDisplay = Quantity;
    } else {
      legSizeForDisplay = getOptionOnlyMultilegDisplayQuantity(leg, Quantity, legs);
    }
  }

  const q = formatNumber(legSizeForDisplay, { isShareField: true });

  const i = optionToDetailString(leg, order, {
    equityShareCountForUnitLabel: leg.AssetType === AssetType.Equity ? legSizeForDisplay : undefined,
  });
  return `${orderAction} to ${e} ${showSymbol ? s : ''} ${q}x ${i}`;
}

/**  This function accepts a string in the
 * format "Symbol DDMMMYY StrikePrice OrderType [quantity]" and converts it to the format shown in the ActivityBlock documentation
 *
 * @param detail a string in the form "Symbol DDMMMYY StrikePrice CALLorPUT [quantity]"
 */
export function detailFormattor(leg: OrderLeg, underlyingSymbol: string): ActivityOrderDetailProps {
  return orderLegToDetails(leg, underlyingSymbol);
}
/**
 * Takes in an OrderStatus value (as a number) and returns
 * A formatted string corresponding to that value
 *
 * @param status an enum value of OrderStatus
 * @returns a string with the corresponding enum value
 */
export function OrderStatusToString(status?: number): string {
  switch (status) {
    case OrderStatuses.New:
      return 'Open';
    case OrderStatuses.PartiallyFilled:
      return 'Partial';
    case OrderStatuses.Filled:
      return 'Filled';
    case OrderStatuses.Cancelled:
      return 'Cancelled';
    case OrderStatuses.Replaced:
      return 'Replaced';
    case OrderStatuses.PendingCancel:
      return 'Pending Cancel';
    case OrderStatuses.Rejected:
      return 'Rejected';
    case OrderStatuses.PendingNew:
      return 'Pending Open';
    case OrderStatuses.Expired:
      return 'Expired';
    case OrderStatuses.PendingReplace:
      return 'Pending Replace';
    case OrderStatuses.Saved:
      return 'Saved';
    case OrderStatuses.Initiated:
      return 'Queued';
    case OrderStatuses.ReplaceInitiated:
      return 'Replace Initiated';
    case OrderStatuses.CancelInitiated:
      return 'Cancel Initiated';
    case OrderStatuses.CancelRejected:
      return 'Cancel Rejected';
    case OrderStatuses.ReplaceRejected:
      return 'Replace Rejected';
    case OrderStatuses.DoneForDay:
      return 'Done For Day';
    case OrderStatuses.Acknowledged:
      return 'Acknowledged';
    case OrderStatuses.PartiallyCancelled:
      return 'Partially Cancelled';
    default:
      return '';
  }
}

/**
 * Some response codes (e.g. 300, 301, 390) use a function for `description` that formats
 * the server message. We must never pass the function to React as a child.
 */
const resolveResponseCodeDescription = (code: ResponseCode, responseMessage: string | undefined): string => {
  const entry = ResponseCodes[code];
  if (entry == null) {
    console.warn('[ActivityStatusMessage] Unknown response code', { code });
    return '';
  }
  const { description } = entry;
  if (typeof description === 'function') {
    return description(responseMessage != null ? responseMessage : '');
  }
  return description;
};

export const ActivityStatusMessage = (activity: OrderActivity): string | undefined => {
  if (isErrorActivity(activity)) {
    if (activity.data.error) {
      return activity.data.error;
    }
    const { Code, Response: orderResponse } = activity.data;
    if (Code != null && ResponseCodes[Code] != null) {
      return resolveResponseCodeDescription(
        Code,
        orderResponse != null ? orderResponse.Message : undefined
      );
    }
    if (orderResponse) {
      return orderResponse.Message;
    }
    return undefined;
  }
  return undefined;
};

/**
 * This function converts a given Order object To a string array
 * to be used in the Messages tab of the activity block
 * @param order the Order you want to convert
 */
export function orderToMessage(order: Order<OrderLeg>) {
  const underlyingSymbol = order.UnderlyingSymbol;
  if (underlyingSymbol === undefined) {
    return [];
  }
  return _.map(order.Legs, val => {
    return formatOrderLeg(val, detailFormattor(val, underlyingSymbol), order, true);
  });
}

/**
 * Maps original status strings to their new display values
 * @param statusString The original status string
 * @returns The new display string
 */
export function mapStatusStringToDisplay(statusString: string): string {
  switch (statusString) {
    case 'New':
      return 'Open';
    case 'Initiated':
      return 'Queued';
    case 'Working':
      return 'Open';
    case 'Pending New':
      return 'Pending Open';
    case 'Acknowledged':
      return 'Open';
    // Return original string if no mapping exists
    default:
      return statusString;
  }
}
